import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiWarningLight } from 'react-icons/pi';
import CurrencyInput from '../../shared/TailwindComponents/CurrencyInput';
import CurrencyPicker from '../../shared/TailwindComponents/CurrencyPicker';
import DateTimePicker from '../../shared/TailwindComponents/DatePicker';
import { ymdToDate, dateToYmd } from '../../Matters/economics/dateHelpers';
import type { UserBillingRate } from '../../../types/economics';

export interface UserRatePayload {
  default_rate: number;
  currency: string;
  effective_from: string;
  effective_to: string | null;
}

interface UserRateModalProps {
  /** Existing rate when editing; null/undefined when setting a new one. */
  rate?: UserBillingRate | null;
  /** The user's own currency - the default for a new rate. */
  defaultCurrency: string;
  /** The firm's default, to warn when the two differ (#2246). */
  orgDefaultCurrency?: string;
  onSave: (payload: UserRatePayload) => Promise<void>;
  onAfterSave?: () => Promise<void> | void;
  /** Reports whether the form can be saved, so the host can disable Save. */
  onValidityChange?: (valid: boolean) => void;
}

export interface UserRateModalHandle {
  handleSave: () => Promise<void>;
}

const todayStr = () => new Date().toISOString().split('T')[0];

/**
 * Set or edit a user's default billing rate (US #2236). Rendered via
 * GlobalModal ('editUserRate') - it used to expand inline inside the My rates
 * section, pushing the history down (feedback round 3).
 */
const UserRateModal = forwardRef<UserRateModalHandle, UserRateModalProps>(
  ({ rate, defaultCurrency, orgDefaultCurrency, onSave, onAfterSave, onValidityChange }, ref) => {
    const { t } = useTranslation();
    const [amount, setAmount] = useState(rate ? String(rate.default_rate ?? '') : '');
    const [currency, setCurrency] = useState(rate?.currency || defaultCurrency);
    const [from, setFrom] = useState(rate?.effective_from || todayStr());
    const [to, setTo] = useState(rate?.effective_to || '');
    const [error, setError] = useState('');

    const futureStart = !!from && from > todayStr();
    const invalidRange = !!to && !!from && to < from;
    const currencyMismatch =
      !!currency && !!orgDefaultCurrency && currency.toUpperCase() !== orgDefaultCurrency.toUpperCase();

    useEffect(() => {
      onValidityChange?.(amount.trim() !== '' && !invalidRange);
    }, [amount, invalidRange, onValidityChange]);

    useImperativeHandle(ref, () => ({
      handleSave: async () => {
        const rateNum = Number(amount);
        if (amount.trim() === '' || Number.isNaN(rateNum) || rateNum <= 0) {
          setError(t('Rate must be greater than 0'));
          throw new Error('invalid');
        }
        if (invalidRange) throw new Error('invalid');
        setError('');
        await onSave({
          default_rate: rateNum,
          currency,
          effective_from: from || todayStr(),
          effective_to: to || null,
        });
        await onAfterSave?.();
      },
    }));

    const warning = (tone: 'warning' | 'danger', text: string) => (
      <div
        className={
          tone === 'warning'
            ? 'flex items-start gap-2 rounded-md border border-warning-300 bg-warning-50 dark:border-warning-700/60 dark:bg-warning-900/20 p-2.5'
            : 'flex items-start gap-2 rounded-md border border-danger-300 bg-danger-50 dark:border-danger-700/60 dark:bg-danger-900/20 p-2.5'
        }
      >
        <PiWarningLight
          className={`h-4 w-4 flex-shrink-0 mt-0.5 ${tone === 'warning' ? 'text-warning-600' : 'text-danger-600'}`}
        />
        <p className={`text-xs ${tone === 'warning' ? 'text-warning-800 dark:text-warning-300' : 'text-danger-800 dark:text-danger-300'}`}>
          {text}
        </p>
      </div>
    );

    return (
      <div className="space-y-4 px-6 text-start">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <CurrencyInput
            id="user-rate-amount"
            label={t('Rate')}
            value={amount}
            currency={currency}
            onChange={setAmount}
            error={error || undefined}
            required
          />
          <CurrencyPicker id="user-rate-currency" label={t('Currency')} value={currency} onChange={setCurrency} />
          <DateTimePicker
            id="user-rate-effective-from"
            label={t('Effective from')}
            selected={ymdToDate(from)}
            showTimeSelect={false}
            dateFormat="yyyy-MM-dd"
            fullWidth
            onChange={(d) => setFrom(dateToYmd(d))}
          />
          <DateTimePicker
            id="user-rate-effective-to"
            // "Open-ended" says what leaving it blank means.
            label={t('Effective to')}
            selected={ymdToDate(to)}
            placeholder={t('Open-ended')}
            showTimeSelect={false}
            dateFormat="yyyy-MM-dd"
            fullWidth
            error={invalidRange ? t('End date must be after start date.') : undefined}
            onChange={(d) => setTo(dateToYmd(d))}
          />
        </div>

        {futureStart &&
          warning(
            'warning',
            t("This rate becomes effective on {{date}}. Time logged before that won't have a rate.", { date: from })
          )}
        {currencyMismatch &&
          warning(
            'danger',
            t(
              "Rate currency ({{rate}}) differs from the organization default ({{org}}). Time on matters in {{org}} won't use this rate.",
              { rate: currency.toUpperCase(), org: (orgDefaultCurrency || '').toUpperCase() }
            )
          )}
      </div>
    );
  }
);

UserRateModal.displayName = 'UserRateModal';

export default UserRateModal;
